import AuthService from '../NewUPT/Security/AuthService';

let refreshPromise = null;

const REFRESH_WHEN_LESS_THAN_MS = 5 * 60 * 1000;

function getToken() {
  return localStorage.getItem('jwt');
}

function getJwtPayload(token) {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((char) => {
          return '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn('Could not parse JWT:', error);
    return null;
  }
}

function getTokenExpirationMs() {
  const token = getToken();
  const payload = getJwtPayload(token);

  if (!payload || !payload.exp) {
    return null;
  }

  return payload.exp * 1000;
}

function shouldRefreshBeforeRequest() {
  const expirationMs = getTokenExpirationMs();

  if (!expirationMs) {
    return false;
  }

  const timeLeft = expirationMs - Date.now();

  return timeLeft > 0 && timeLeft <= REFRESH_WHEN_LESS_THAN_MS;
}

function isTokenAlreadyExpired() {
  const expirationMs = getTokenExpirationMs();

  if (!expirationMs) {
    return false;
  }

  return Date.now() >= expirationMs;
}

async function refreshTokenOnce() {
  if (!refreshPromise) {
    const authService = new AuthService();

    refreshPromise = authService.refreshToken()
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

async function refreshIfNeededBeforeRequest() {
  if (!getToken()) {
    return;
  }

  if (shouldRefreshBeforeRequest()) {
    await refreshTokenOnce();
  }
}

export async function authFetch(url, options = {}) {
  try {
    await refreshIfNeededBeforeRequest();
  } catch (error) {
    console.warn('Pre-request token refresh failed:', error);

    if (isTokenAlreadyExpired()) {
      return new Response(
        JSON.stringify({ error: 'unauthorized' }),
        {
          status: 401,
          headers: { 'Content-Type': 'application/json' },
        }
      );
    }
  }

  const token = getToken();
  const originalHeaders = options.headers || {};

  const headers = {
    ...originalHeaders,
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  let response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status !== 401) {
    return response;
  }

  try {
    await refreshTokenOnce();

    const newToken = getToken();

    const retryHeaders = {
      ...originalHeaders,
      ...(newToken && { Authorization: `Bearer ${newToken}` }),
    };

    response = await fetch(url, {
      ...options,
      headers: retryHeaders,
    });

    return response;
  } catch (error) {
    console.warn('Token refresh failed after 401:', error);

    return response;
  }
}